import { useEffect } from "react";
import type { ReactElement, ReactNode } from "react";
import { useTelefon } from "@kundeninfo/ui";
import { Reiter } from "@kundeninfo/ui/me-design";
import { KiRegister } from "../../components/meHuellen.js";
import { SUPPLIER_AREAS } from "../../router.js";
import type { SupplierArea } from "../../router.js";
import { SUPPLIER_TEXT_WEB } from "../suppliers/texts.js";
import { INCOMING_TEXT_WEB } from "../incomingInvoices/texts.js";
import { rememberPurchasingTab } from "./purchasingTab.js";
import "./Purchasing.css";

/** Die Reiter des Einkaufs: je Bereich (Reihenfolge `SUPPLIER_AREAS`) die Kennung des Panels (`fuer` am Reiter, `id` am
 *  Panel; Muster `BereichTickets`), die Beschriftung und ihr Kurzlabel für das Telefon (Entscheid 522; im Langlabel
 *  enthalten, WCAG 2.5.3 „Label in Name“). Der Name des Reiters bleibt auch am Telefon das Langlabel: `aria-label` am
 *  Reiter, `me-reiter` spiegelt es an seinen Knopf (`role="tab"`, me-design fbd4cb3). */
export const PURCHASING_TABS: Record<SupplierArea, { panel: string; label: string; shortLabel: string }> = {
  liefervertraege: {
    panel: "purchasing-liefervertraege", label: SUPPLIER_TEXT_WEB.area, shortLabel: SUPPLIER_TEXT_WEB.areaShort,
  },
  eingangsrechnungen: {
    panel: "purchasing-eingangsrechnungen", label: INCOMING_TEXT_WEB.area, shortLabel: INCOMING_TEXT_WEB.areaShort,
  },
};

/**
 * DER BEREICH „EINKAUF“ (Dirk-Entscheid 516b, 30.10.2026): ein Navigationseintrag mit den Reitern
 * „Lieferverträge | Eingangsrechnungen“ (`me-register`, Hausregel „Reiter nach unten offen“: der aktive Reiter und das
 * Panel sind eine Fläche, Reiter in Inhaltsbreite). Darunter steht der Bereich des aktiven Reiters unverändert
 * (`BereichLiefervertraege`, `BereichEingangsrechnungen`, von App als Kind gereicht).
 *
 * Die Adresse bleibt die des Bereichs (`/liefervertraege`, `/eingangsrechnungen`): Kacheln, Handlungsbedarf, Rückwege und
 * Akte springen weiter dorthin. Gemerkt wird der ANGEZEIGTE Reiter (Fixrunde 1, Ruling W1) — nach einem Reiterwechsel
 * ebenso wie nach Direktaufruf, Browser-Zurück oder Rückweg aus der Akte, und erst, wenn er wirklich dasteht: Lehnt ein
 * Verlassen-Wächter den Wechsel ab, bleibt der gemerkte Reiter der sichtbare. Der Klick auf den aktiven Reiter navigiert
 * nicht — sonst verlöre die Adresse ihre Status-Chips. Die Leiste steht an derselben Stelle des Baums, egal welcher
 * Reiter aktiv ist: Sie wird beim Wechsel nicht neu gebaut und kippt nicht.
 */
export function PurchasingArea(p: {
  active: SupplierArea; userId: string | undefined; onSelect: (tab: SupplierArea) => void; children: ReactNode;
}): ReactElement {
  useEffect(() => { rememberPurchasingTab(p.active, p.userId); }, [p.active, p.userId]);
  const select = (panelId: string): void => {
    const tab = SUPPLIER_AREAS.find((t) => PURCHASING_TABS[t].panel === panelId);
    if (tab !== undefined && tab !== p.active) p.onSelect(tab);
  };
  const shown = PURCHASING_TABS[p.active];
  const phone = useTelefon();
  return (
    <div className="pur">
      <KiRegister className="pur__tabs" aria-label={SUPPLIER_TEXT_WEB.purchasingTabs} aktiv={shown.panel}
        onReiterGewaehlt={(d) => select(d.id)}>
        {SUPPLIER_AREAS.map((t) => (
          <Reiter key={t} fuer={PURCHASING_TABS[t].panel} aria-label={PURCHASING_TABS[t].label}
            beschriftung={phone ? PURCHASING_TABS[t].shortLabel : PURCHASING_TABS[t].label} />
        ))}
      </KiRegister>
      <div className="pur__panel" id={shown.panel} role="tabpanel" aria-label={shown.label}>
        {p.children}
      </div>
    </div>
  );
}
